import classNames from 'classnames';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetch } from 'loti-request';
import { debounce } from 'lodash';

import Input from 'app/components/Input';
import Dropdown from 'app/components/Dropdown';
import { getPandapiHeaders, getPandapiUrl } from 'app/utils/api';

import style from './style.scss';

interface Props {
  value: number | null;
  onChange: (champId: number | null) => void;
  disabled?: boolean;
  onlyLabel?: boolean;
}

const LolChampionInput: FC<Props> = ({ value, onChange, disabled, onlyLabel }) => {
  const [t] = useTranslation();

  const [opened, setOpened] = useState(false);
  const [champInput, setChampInput] = useState('');
  const [champFilter, setChampFilter] = useState('');

  const handleConfirm = (champId: number | null) => {
    onChange(champId);
    setOpened(false);
  };

  const debounceFilter = useMemo(
    () =>
      debounce(
        name => {
          setChampFilter(name);
        },
        500,
        {}
      ),
    []
  );

  const handleSearch = (name: string) => {
    setChampInput(name);
    debounceFilter(name);
  };

  const { data: selectedChampion } = useFetch<MobaChampion[]>({
    url: getPandapiUrl(`/lol/champions?filter[id]=${value}`),
    headers: getPandapiHeaders(),
    preventRequest: !value
  });

  const { data: champions } = useFetch<MobaChampion[]>({
    url: getPandapiUrl(`/lol/champions?search[name]=${champFilter}&sort=name`),
    headers: getPandapiHeaders(),
    preventRequest: !champFilter
  });

  if (onlyLabel) {
    return <>{selectedChampion?.[0]?.name || t('component-lol_champion_dropdown-champion')}</>;
  }

  return (
    <Dropdown
      label={selectedChampion?.[0]?.name || t('component-lol_champion_dropdown-champion')}
      width={210}
      dropdownClassName={style.portal}
      withIcon={false}
      opened={opened}
      onOpenChange={openedValue => setOpened(openedValue)}
      disabled={disabled}
      variation="inline"
      portalClassName={style.portalContainer}
    >
      <div className={style.dropdownContainer}>
        <Input
          value={champInput}
          onChange={event => handleSearch(event.currentTarget.value)}
          placeholder={t('common-search')}
          className={style.championInput}
        />

        {!champions?.length && selectedChampion?.[0] && (
          <div className={classNames(style.championOption, style.selected)}>
            <img src={selectedChampion[0].image_url} alt="" />
            <span>{selectedChampion[0].name}</span>
          </div>
        )}

        {(champions || []).map(champ => {
          const selected = champ.id === value;
          return (
            <div
              className={classNames(style.championOption, { [style.selected]: selected })}
              onClick={() => handleConfirm(selected ? null : champ.id)}
              key={champ.id}
            >
              <img src={champ.image_url} alt="" />
              <span>{champ.name}</span>
            </div>
          );
        })}
      </div>
    </Dropdown>
  );
};

const ControlledLolChampionInput: FC<Props> = ({ onChange, value, ...props }) => {
  const [selectedChampionId, setSelectedChampionId] = useState<number | null>(value);

  const handleConfirm = (value: number | null) => {
    setSelectedChampionId(value);
    onChange(value);
  };

  useEffect(() => {
    setSelectedChampionId(value);
  }, [value]);

  return <LolChampionInput value={selectedChampionId} onChange={handleConfirm} {...props} />;
};

export { LolChampionInput, ControlledLolChampionInput };
